import React, { useEffect, useMemo, useState } from 'react';
import SectionHeader from '@components/common/SectionHeader';
import FooterBrand from '@components/common/FooterBrand';
import DividendControls from './DividendControls';
import DividendHistoryChart from '@components/charts/DividendHistoryChart';
import DividendLegend from './DividendLegend';
import DividendMatrixTable from './DividendMatrixTable';
import { getDividendChartPage } from './dividendChartPagination';
import {
  getDividendStockPriceDateRange,
  mapDividendTransitionApiToTrends,
} from '@apis/dividendMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useDividendTransition } from '@hooks/useDividendTransition';
import { useDividendStockPriceHistory } from '@hooks/useDividendStockPriceHistory';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { notify } from '@utils/notify';
import type {
  DividendTableRow,
  DividendTrendsSectionData,
} from '@app-types/performanceDividend';
import type { DividendCurrencyCode } from '@app-types/dividendApi';

export interface DividendTrendSectionProps {
  data: DividendTrendsSectionData;
  /**
   * BA-approved screen-level display currency from `/dividend-info`. Display-only:
   * it re-renders trend units, history headers, and chart axes without refetching
   * transition or Chart5 data. Missing/unknown keeps the established JPY display.
   */
  currency?: DividendCurrencyCode | null;
  /** Instrument market for BA-approved Special/capital-gains labels. */
  market?: string | null;
  /** Instrument identity for local pagination reset; never used for messaging. */
  ticker?: string | null;
}

interface ChartPageChoice {
  contextKey: string;
  manualPage: number | null;
}

const toPeriodCount = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
};

const buildVisibleRows = (
  rows: DividendTableRow[],
  expandedKeys: Set<string>,
): DividendTableRow[] =>
  rows.flatMap((row) => {
    const hasChildren = !!row.children?.length;
    const expanded = hasChildren && expandedKeys.has(row.key);
    const parentRow: DividendTableRow = {
      ...row,
      disclosure: hasChildren ? (expanded ? 'up' : 'down') : undefined,
    };

    if (!expanded || !row.children) {
      return [parentRow];
    }

    return [
      parentRow,
      ...row.children.map((child) => ({
        ...child,
        disclosure: undefined,
        children: undefined,
      })),
    ];
  });

/**
 * Full dividend trends section.
 * Period selector controls latest 10/latest 5 periods; disclosure arrows
 * expand/collapse interim dividend rows for the selected annual period.
 */
const DividendTrendSection: React.FC<DividendTrendSectionProps> = ({ data, currency, market, ticker }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(data.controls.selectedPeriod);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(() => new Set());
  // Mobile chart pagination is local UI state only; it never refetches
  // transition/Chart5 data or changes React Query keys (work_items_96).
  const { isMobile } = useBreakpoint();
  const pageContextKey = JSON.stringify([ticker ?? '', market ?? '', selectedPeriod, isMobile]);
  const [pageChoice, setPageChoice] = useState<ChartPageChoice>(() => ({
    contextKey: pageContextKey,
    manualPage: null,
  }));

  const years = toPeriodCount(selectedPeriod);
  const transitionQuery = useDividendTransition(years);
  const transitionRows = transitionQuery.data?.rows;

  // Blue stock-price line: derive the visible annual range from the transition
  // data, then fetch the chart5-hts CSV. Hook stays unconditional; `null`
  // range keeps it disabled until enough annual rows are available.
  const stockPriceRange = useMemo(
    () => getDividendStockPriceDateRange(transitionRows, { years }),
    [transitionRows, years],
  );
  const stockPriceQuery = useDividendStockPriceHistory(
    stockPriceRange?.from ?? null,
    stockPriceRange?.to ?? null,
  );

  // API-backed trends (chart, annual/quarterly rows, bottom history). Static
  // metadata (titles, columns, node IDs, controls) comes from `data`. The
  // stock-price CSV only augments the chart line; a missing/failed stock query
  // leaves bars and tables intact with null line points. The response `nameJp`
  // drives the blue stock series/legend label (missing name -> `--`).
  const trendsData = useMemo(
    () =>
      mapDividendTransitionApiToTrends(transitionRows, data, {
        years,
        stockPriceCsv: stockPriceQuery.data,
        companyName: transitionQuery.data?.nameJp,
        currency,
        market,
      }),
    [transitionRows, data, years, stockPriceQuery.data, transitionQuery.data?.nameJp, currency, market],
  );

  useEffect(() => {
    if (transitionQuery.isError && shouldNotifyDataRequestError(transitionQuery.error)) {
      notify({
        level: 'error',
        message:
          getApiParamErrorNotificationMessage(transitionQuery.error) ??
          '配当推移の取得に失敗しました。',
      });
    }
  }, [transitionQuery.isError, transitionQuery.error]);

  // Pagination is derived from the full selected dataset. Axis bounds stay on
  // the mapper output so scale stays comparable between pages.
  const requestedPage = pageChoice.contextKey === pageContextKey ? pageChoice.manualPage : null;
  const chartPage = useMemo(
    () => getDividendChartPage(trendsData.chart.points, requestedPage),
    [trendsData.chart.points, requestedPage],
  );

  // Derivation above applies the new default immediately; commit the context so
  // A -> B -> A cannot revive a previous manual selection (work_items_136).
  useEffect(() => {
    setPageChoice((current) => current.contextKey === pageContextKey
      ? current
      : { contextKey: pageContextKey, manualPage: null });
  }, [pageContextKey]);

  // A transient loading/error state must not permanently clamp a manual page.
  // Automatic latest intent stays null even for a successful empty response.
  const hasResolvedRows = transitionQuery.isSuccess && Array.isArray(transitionRows);
  useEffect(() => {
    if (!hasResolvedRows) return;
    setPageChoice((current) => {
      if (current.contextKey !== pageContextKey || current.manualPage === null) return current;
      const manualPage = Math.min(Math.max(current.manualPage, 1), chartPage.pageCount);
      return manualPage === current.manualPage ? current : { ...current, manualPage };
    });
  }, [pageContextKey, hasResolvedRows, chartPage.pageCount]);

  // Desktop shows every selected point; mobile shows the five-point slice.
  const chartData = useMemo(
    () =>
      isMobile
        ? { ...trendsData.chart, points: chartPage.points }
        : trendsData.chart,
    [isMobile, trendsData.chart, chartPage.points],
  );

  const trendTableData = useMemo(
    () => ({
      ...trendsData.trendsTable,
      rows: buildVisibleRows(trendsData.trendsTable.rows, expandedRowKeys),
    }),
    [trendsData.trendsTable, expandedRowKeys],
  );

  const handleToggleRow = (row: DividendTableRow) => {
    if (!row.children?.length) {
      return;
    }

    setExpandedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(row.key)) {
        next.delete(row.key);
      } else {
        next.add(row.key);
      }
      return next;
    });
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    setExpandedRowKeys(new Set());
  };

  const handlePreviousChartPage = () => {
    setPageChoice({ contextKey: pageContextKey, manualPage: Math.max(chartPage.page - 1, 1) });
  };

  const handleNextChartPage = () => {
    setPageChoice({ contextKey: pageContextKey, manualPage: Math.min(chartPage.page + 1, chartPage.pageCount) });
  };

  return (
    <section className="dividend-trends" data-node-id={data.sectionNodeId}>
      <SectionHeader
        title={data.title}
        showHelp
        dataNodeId={data.headerNodeId}
        as="h2"
      />
      <div className="dividend-trends__panel" data-node-id={data.panelNodeId}>
        <DividendControls
          data={data.controls}
          nodeId="3158:7871"
          selectedPeriod={selectedPeriod}
          onPeriodChange={handlePeriodChange}
          pageRangeLabel={chartPage.rangeLabel}
          pageLabel={chartPage.pageLabel}
          onPreviousPage={handlePreviousChartPage}
          onNextPage={handleNextChartPage}
          previousDisabled={!chartPage.hasPrevious}
          nextDisabled={!chartPage.hasNext}
        />

        <div className="dividend-chart-card" data-node-id={data.chartCardNodeId}>
          <div className="dividend-chart" data-node-id={data.chart.nodeId}>
            <DividendHistoryChart data={chartData} height={198} />
          </div>

          <DividendLegend items={trendsData.legend} nodeId={data.legendNodeId} />
        </div>

        <div className="dividend-table-section" data-node-id={data.trendsTable.nodeId}>
          <DividendMatrixTable data={trendTableData} onToggleRow={handleToggleRow} />
        </div>
      </div>

      <FooterBrand
        className="dividend-trends__footer"
        nodeId={data.footerNodeId}
        markNodeId={data.footerLogoNodeId}
      />
    </section>
  );
};

export default DividendTrendSection;
